/**
 * New Relic's SPA agent (nr-loader-spa) throws:
 *   TypeError: Cannot read properties of undefined (reading 'startTime')
 *   at et.reportAllChanges
 * That runs on a timer (n.timeout), so it still fires after a cached agent load.
 * Swallow only that agent bug — do not hide real app errors.
 */
const NR_START_TIME_ERROR =
  /Cannot read properties of undefined \(reading ['"]startTime['"]\)/;

function isLocalBrowserHost(host: string): boolean {
  const h = (host || "").toLowerCase();
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".localhost") ||
    h.startsWith("192.168.") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(h)
  );
}

/** Local + company-dev (park.dev.criticalasset.com). Staging/prod still load RUM. */
function shouldSkipNewRelicAgent(host: string): boolean {
  const h = (host || "").toLowerCase();
  if (isLocalBrowserHost(h)) return true;
  return h.includes(".dev.") || h.includes("-dev.");
}

export { isLocalBrowserHost, shouldSkipNewRelicAgent };

function suppressAgentStartTimeError(event: ErrorEvent): void {
  const msg = event.message || "";
  const stack = event.error?.stack || "";
  const fromAgent =
    NR_START_TIME_ERROR.test(msg) &&
    (stack.includes("reportAllChanges") || !event.filename);
  if (!fromAgent) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}

export function installNewRelicGuard(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", suppressAgentStartTimeError, true);
  const previousOnError = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (typeof message === "string" && NR_START_TIME_ERROR.test(message)) return true;
    if (typeof previousOnError === "function") {
      return previousOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  if (!shouldSkipNewRelicAgent(window.location.hostname)) return;

  const w = window as unknown as { NREUM?: { addPageAction?: (...args: unknown[]) => void } };
  if (w.NREUM) {
    w.NREUM.addPageAction = () => undefined;
  }
}

installNewRelicGuard();
